<script setup lang="tsx">
import type { MitigationMeasure } from '@/models/api-generated'
import { daysUntilDate, getResponsibleIcon } from '@/services/utils'
import { formatPrice, notApplicableGroup, statusColumns } from '../shared'
import { ConditionallyImplemented, Implemented, WillNotApply, WillNotImplement, type MethodStatus } from '@/models/enums-generated'
import { computed } from 'vue'
import DueDateLabel from '@/components/widgets/DueDateLabel.vue'
import IconButton from '@/components/shared/IconButton.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
	isEdit: boolean
	onDelete: (id: number) => void
	deletedIds: Set<number>
	assetId: number
}>()

const { t } = useI18n()

const model = defineModel<MitigationMeasure>({ required: true })

const getStatusColumnKey = (status?: MethodStatus) => {
	if (!status) return
	if ([WillNotApply, WillNotImplement].includes(status)) return notApplicableGroup
	if (status === ConditionallyImplemented) return Implemented
	return status
}

const getPriorityClass = (priority?: string) => {
	switch (priority) {
		case 'low':
			return ['pie1', 'bi-chevron-down']
		case 'medium':
			return ['pie2', 'bi-dash-lg']
		case 'high':
			return ['pie3', 'bi-chevron-up']
		default:
			return []
	}
}

const daysToDueDate = computed(() => {
	if (model.value.dueDate) {
		return daysUntilDate(model.value.dueDate)
	}
	return null
})

function goToMethodsPage(status?: string, name?: string) {
	if (!name || !status) return
	const params = new URLSearchParams({
		reference: name,
		...(status && { status }),
	})
	window.location.href = `/app/methods?${params}`
}
</script>

<template>
	<tr
		class="subrow"
		:key="model.method.name"
		:class="{
			'table-danger': deletedIds.has(model.id),
			'table-info': model.isCustom && model.id === 0,
		}"
	>
		<td
			class="text-center risks-cell"
			@click="goToMethodsPage(model.status, model.method.name)"
			v-one-tooltip="!model.isApplicable ? '' : $t('risk.mitigation.filter')"
		>
			<i class="bi bi-arrow-return-right"></i>
		</td>
		<td
			colspan="2"
			:class="{ 'text-muted fw-bold': !model.isApplicable }"
			v-one-tooltip="model.isApplicable ? '' : $t('risk.mitigation.addConnections')"
		>
			{{
				model.isApplicable
					? `${model.method.name} -  ${model.method.description}`
					: `${model.method.name} - ${t('risk.mitigation.willNotImplement')}`
			}}
		</td>
		<td></td>
		<td v-for="s in statusColumns" :key="s.key">
			<i v-if="model.isApplicable && getStatusColumnKey(model.status) === s.key" :class="['bi bi-square-fill', s.color]"></i>
		</td>
		<td class="text-nowrap">
			<template v-if="model.assetId">
				<span class="badge bg-primary me-2">A</span><span>{{ model.assetName }}</span>
			</template>
			<template v-else>
				<span v-one-tooltip="!props.assetId ? '' : $t('risk.mitigation.assetModuleRemoved')">-</span>
			</template>
		</td>
		<td>{{ formatPrice(model.price) }}</td>
		<td><i :class="['bi', ...getPriorityClass(model.priority)]"></i></td>
		<td>
			<template v-if="model.status === Implemented || model.status === ConditionallyImplemented">-</template>
			<template v-else> <DueDateLabel :days-to-due-date="daysToDueDate"></DueDateLabel></template>
		</td>
		<td class="text-nowrap container-end">
			<i v-if="model.responsible" :class="[getResponsibleIcon(model.responsible)]"></i>{{ model.responsible?.label }}
		</td>
		<td v-if="props.isEdit && model.isCustom" class="delete-cell align-middle">
			<IconButton
				data-test="new-mitigation-measure-delete-button"
				class="bi bi-trash text-danger"
				@click="props.onDelete(model.id!)"
				:title="$t('misc.delete')"
				size="fs-5"
			/>
		</td>
	</tr>
</template>

<style lang="sass" scoped>
.subrow td:not(:first-child):not(.delete-cell)
  background-color: var(--bs-body-bg)
  border-top: 1px solid var(--bs-border-color)
  border-bottom: 1px solid var(--bs-border-color)

.subrow.table-danger td:not(:first-child):not(.delete-cell)
  background-color: var(--bs-danger-bg-subtle) !important
  color: var(--bs-body-color) !important

tr.subrow td:nth-child(2)
  border-top-left-radius: .5rem
  border-bottom-left-radius: .5rem
  border-left: 1px solid var(--bs-border-color)

.container-end
  border-top-right-radius: .5rem
  border-bottom-right-radius: .5rem
  border-right: 1px solid var(--bs-border-color)

th,
td
  padding: .5rem 1rem

.subrow td.delete-cell
  background: transparent
  border: 0
  padding: 0 .5rem
  width: 44px
  text-align: right

.subrow.table-info td:not(:first-child):not(.delete-cell)
  background-color: var(--bs-info-bg-subtle) !important
  color: var(--bs-body-color) !important
</style>
